import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector, useCurrentUser } from 'hooks';
import {
  isPreRegistrationKey,
  PreRegistrationTokenSnapshot,
} from 'services/preRegistrationTokens';
import LayoutSignUp from 'components/layouts/LayoutSignUp';
import { getUrlParams } from 'utils/url';
import path from 'common/path';
import {
  deleteBusinessesId,
  hasOtpSession,
  getUserEmail,
  setUserEmail,
  setProfileId,
} from 'utils/authService';

import ProfileContainer from './components/ProfileContainer';
import { stashEnrollPrefill, takeEnrollPrefill } from './prefill';
import { getOnboarding, getCountries } from './selectors';
import {
  fetchOrCreatePerson,
  fetchPreRegistrationPrefill,
  fetchCountries,
} from './thunks';
import {
  resetState,
  setIsLoading,
  setRedirectToDashboard,
  applyEnrollPrefill,
} from './profileSlice';

const Profile = () => {
  const dispatch = useAppDispatch();
  const location: any = useLocation();
  const { user, isAuthenticated } = useCurrentUser();

  const onboarding = useAppSelector(getOnboarding);
  const countries = useAppSelector(getCountries);
  const params: any = getUrlParams();
  const otpSession = hasOtpSession();

  const { businessId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (businessId) {
      setProfileId(+businessId);

      if (params?.$email) {
        setUserEmail(params.$email);
      }

      navigate(path.sso);
      return;
    }

    // the visitor came from the sign-up form: the profile loaded after the code check, so fill the prefill now
    // a 39196 key (stashed by the signed-out visitor) pulls the pre-registration snapshot now that they are signed in;
    // it is skipped when the person already has a business so it cannot overwrite saved data
    const restorePrefill = (email: string, loadedPayload: any) => {
      const prefill = takeEnrollPrefill(email);

      if (!prefill) {
        return;
      }

      if (prefill.key) {
        if (!loadedPayload?.business?.businessesId) {
          dispatch(fetchPreRegistrationPrefill(prefill.key));
        }
        return;
      }

      dispatch(applyEnrollPrefill(prefill));
    };

    if (!onboarding && (isAuthenticated || otpSession)) {
      let email = user?.username || getUserEmail();

      if (!email && otpSession && params?.$email) {
        email = params.$email;
        dispatch(setRedirectToDashboard(false));
      }

      if (email) {
        const loaded = dispatch(
          fetchOrCreatePerson({
            username: email,
            name: user?.name || '',
            checkStep: location?.state?.checkStep ?? true,
          }),
        );

        Promise.resolve(loaded).then((action: any) =>
          restorePrefill(email, action?.payload),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onboarding, user, location, isAuthenticated]);

  useEffect(() => {
    const resetProfile = async () => {
      deleteBusinessesId();
      await dispatch(resetState(user));
    };

    if (onboarding) {
      resetProfile();
    }
  }, [dispatch, onboarding, user]);

  useEffect(() => {
    if (!countries.length) {
      dispatch(fetchCountries());
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    // not signed in: the email code comes first. Send the visitor to the sign-up form with their email (no
    // message: nothing says the email is taken), and keep the prefill for when the code has been checked
    const sendToEnroll = async () => {
      // 39196 - a pre-registration link (?key=<guid>) is only read here to learn the account email; the prefill
      // itself is applied after sign-in. An expired/missing/invalid key, or one with no email -> fresh onboarding.
      if (isPreRegistrationKey(params?.$key)) {
        const keyAction = await dispatch(
          fetchPreRegistrationPrefill(params.$key),
        );
        const snapshot =
          keyAction.payload as PreRegistrationTokenSnapshot | null;
        const accountEmail =
          snapshot?.contactEmail || snapshot?.mainBusinessEmail;

        if (accountEmail) {
          stashEnrollPrefill({ $email: accountEmail, $key: params.$key });
          navigate(`${path.enroll}?email=${encodeURIComponent(accountEmail)}`, {
            replace: true,
          });
          return;
        }

        dispatch(setIsLoading(false));
        return;
      }

      if (params?.$email == null) {
        dispatch(setIsLoading(false));
        return;
      }

      stashEnrollPrefill(params);
      navigate(`${path.enroll}?email=${encodeURIComponent(params.$email)}`, {
        replace: true,
      });
    };

    // the Hub profile link (/profile/:businessId) signs in through /sso, not the email code
    if (!businessId && !isAuthenticated && !otpSession) {
      dispatch(setRedirectToDashboard(false));
      sendToEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <LayoutSignUp>
      <ProfileContainer />
    </LayoutSignUp>
  );
};

export default Profile;
